<template>
    <div class="dropdown-select">
        <!-- Call to action div with selected label -->
        <div
            class="dropdown-select__trigger p-sm display-flex align-items-center justify-between"
            @click="toggleDropdown"
        >
            <p class="dropdown-select__selected text-stone-800 text-truncate pl-xs">
                {{ selectedOption ? selectedOption.label : placeholder }}
            </p>
            <font-awesome-icon
                class="dropdown-select__chevron text-secondary"
                icon="fas fa-chevron-down"
                size="sm"
                :class="{ 'dropdown-select__chevron--rotated': isOpen }"
            />
        </div>

        <transition name="dropdown-fade">
            <div
                v-if="isOpen"
                class="dropdown-select__options elevation-4"
                v-click-outside="
                    () => {
                        if (isOpen) {
                            isOpen = false
                        }
                    }
                "
            >
                <div
                    v-for="(option, index) in options"
                    :key="index"
                    class="dropdown-select__option px-xs display-flex align-items-center"
                    @click="selectOption(option)"
                >
                    <div
                        class="dropdown-select__option-inner display-flex align-items-center p-sm w-100"
                        :class="{
                            'dropdown-select__option-inner--selected': isOptionSelected(option),
                        }"
                    >
                        <div class="dropdown-select__check display-flex align-items-center">
                            <font-awesome-icon
                                v-if="isOptionSelected(option)"
                                class="dropdown-select__check-icon text-stone-800"
                                icon="fas fa-check"
                            />
                        </div>
                        <p class="font-base text-stone-800">{{ option.label }}</p>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { ref, watch, computed, defineEmits } from 'vue'

const props = defineProps({
    options: {
        type: Array,
        default: () => [],
    },
    modelValue: {
        type: [String, Number, Object],
        default: null,
    },
    placeholder: {
        type: String,
        default: 'Select an option',
    },
})

const emit = defineEmits(['update:modelValue', 'change'])

const isOpen = ref(false)
const selectedOption = ref(null)

// Find the initially selected option based on modelValue
watch(
    () => props.modelValue,
    (newValue) => {
        if (newValue) {
            selectedOption.value = props.options.find(
                (option) => option.value === newValue || option === newValue
            )
        } else {
            selectedOption.value = null
        }
    },
    { immediate: true }
)

// Toggle dropdown visibility
const toggleDropdown = () => {
    isOpen.value = !isOpen.value
}

const isOptionSelected = (option) => {
    if (!selectedOption.value) return false

    return option.value !== undefined
        ? option.value === selectedOption.value.value
        : option === selectedOption.value
}

// Handle option selection
const selectOption = (option) => {
    selectedOption.value = option
    isOpen.value = false

    // Emit the selected value (support for v-model)
    emit('update:modelValue', option.value !== undefined ? option.value : option)

    // Also emit a change event with the full option object
    emit('change', option)
}
</script>

<style scoped lang="scss">
@forward '@/styles/new_styles.scss';

.dropdown-select {
    position: relative;
    width: 100%;
    max-width: 250px;

    &__trigger {
        height: 50px;
        border: 1px solid var(--color-stone-400);
        border-radius: var(--border-radius-lg);
        background: var(--color-stone-0);
        cursor: pointer;
        box-shadow:
            0px 1px 1px -0.5px var(--color-stone-200),
            0px 3px 3px -1.5px var(--color-stone-200);
    }

    &__chevron {
        transition: transform 0.3s ease-in-out;

        &--rotated {
            transform: rotate(180deg);
        }
    }

    &__options {
        position: absolute;
        top: calc(100% + 10px);
        left: 0;
        width: 100%;
        max-height: 200px;
        overflow-y: auto;
        background-color: var(--color-stone-0);
        border-radius: var(--border-radius-lg);
        border: 1px solid var(--color-stone-300);
        z-index: 12;
        box-shadow: var(--shadow-md);
        padding-top: var(--spacing-sm);
    }

    &__option {
        padding-bottom: var(--spacing-sm);
    }

    &__option-inner {
        cursor: pointer;
        border-radius: var(--border-radius-sm);
        transition: all 0.3s ease-in-out;

        &:hover {
            background-color: var(--color-stone-200);
        }

        &--selected {
            background-color: var(--color-stone-200);
        }
    }

    &__check {
        min-width: 25px;
    }

    .dropdown-fade-enter-active,
    .dropdown-fade-leave-active {
        transition:
            opacity 0.3s,
            transform 0.3s;
    }

    .dropdown-fade-enter-from,
    .dropdown-fade-leave-to {
        opacity: 0;
        transform: translateY(-10px);
    }
}
</style>
